import React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Tab {
  id: string;
  title: string;
  active?: boolean;
  closable?: boolean;
}

interface BrowserWindowProps {
  tabs: Tab[];
  activeTabId: string;
  onTabClick: (id: string) => void;
  onTabClose: (id: string) => void;
  children: React.ReactNode;
  className?: string;
  stickyHeader?: React.ReactNode;
  hideTabs?: boolean;
}

/**
 * Browser Window — wraps a consultation tab's content with a tab bar and content area.
 * Uses shadcn Tabs for the tab bar. Closable tabs get an X button.
 */
export const BrowserWindow = ({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  children,
  className,
  stickyHeader,
  hideTabs = false,
}: BrowserWindowProps) => {
  return (
    <div
      className={cn(
        "flex flex-col h-full bg-card rounded-3xl border border-border/60 shadow-sm overflow-hidden",
        className
      )}
    >
      {/* Tab Bar */}
      {!hideTabs && tabs.length > 0 && (
        <div className="shrink-0 border-b border-border/60 bg-muted/30 px-4 py-1.5">
          <Tabs value={activeTabId} onValueChange={onTabClick}>
            <TabsList className="h-7 bg-muted/50 p-0.5 gap-0.5">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="z-secondary font-medium gap-1.5 max-w-[200px] h-6 px-3 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                >
                  <span className="truncate">{tab.title}</span>
                  {tab.closable && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTabClose(tab.id);
                      }}
                      className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                      aria-label={`Close ${tab.title}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Optional sticky header */}
      {stickyHeader}

      {/* Content */}
      <div className="flex-1 overflow-hidden relative bg-surface">
        {children}
      </div>
    </div>
  );
};
